import Phaser from '../lib/phaser.js';
import { SCENE_KEYS } from './scene-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { DIRECTION } from '../common/direction.js';

// ── On-screen touch controls overlay (runs OVER the world, like the other HUD scenes) ──
// Phones have no keyboard, so overworld movement (keyboard/WASD only) is impossible without this.
// This scene renders a virtual joystick (bottom-left) + a round "A" action button (bottom-right)
// and feeds the WorldScene's Controls via setVirtualDirection() / pressVirtualConfirm() — so NO
// world movement code changes: the world keeps reading `this._controls` exactly as before.
//
// scrollFactor 0 + high depth so it pins to the screen and sits above the world/HUD. It never
// pauses the world (it's a parallel overlay) and never blocks the existing HUD buttons (joystick
// sits in the empty bottom band; the menu/cards are top-left / bottom-right of their own).
//
// Style: emerald glass, semi-transparent so it doesn't obscure the world.

const EMERALD = 0x34d399;
const GLASS = 0x051610;
const DEPTH = 1100; // above the world HUD (1000)

// Base sizes (px). Scaled down on narrow viewports so they stay thumb-sized but never huge.
const JOY_BASE_R = 70; // ~140px base circle
const JOY_THUMB_R = 30; // ~60px thumb
const ACTION_R = 40; // ~80px "A" button
const MENU_R = 26; // smaller "≡" menu button
const MARGIN = 26; // distance from the LEFT/RIGHT screen edges
// Extra bottom inset so the controls clear the iOS home-indicator + the bottom HUD bands (party bar
// bottom-left, quest panel bottom-right). Honors the CSS safe-area inset when present.
const BOTTOM_MARGIN = 34;
const DEADZONE = 14; // px the thumb must move before a direction registers

/** Read the iOS safe-area bottom inset (px) so the controls never sit under the home indicator. */
function safeAreaBottom() {
  if (typeof window === 'undefined' || typeof getComputedStyle === 'undefined' || typeof document === 'undefined') {
    return 0;
  }
  // index.html uses `viewport-fit=cover`, so env(safe-area-inset-bottom) is live on notched devices.
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;bottom:env(safe-area-inset-bottom,0px);height:0;width:0;pointer-events:none;';
  document.body.appendChild(probe);
  const inset = Math.max(0, Math.round(parseFloat(getComputedStyle(probe).bottom) || 0));
  probe.remove();
  return inset;
}

export class MobileControlsScene extends Phaser.Scene {
  /** @type {Phaser.GameObjects.Container} joystick container (base + thumb) */
  #joy;
  /** @type {Phaser.GameObjects.Arc} draggable thumb */
  #thumb;
  /** @type {Phaser.GameObjects.Container} "A" action button */
  #actionBtn;
  /** @type {Phaser.GameObjects.Container} "≡" menu button */
  #menuBtn;
  /** @type {number} live render scale for the controls (shrinks on small viewports) */
  #scale = 1;
  /** @type {boolean} joystick drag in progress */
  #dragging = false;
  /** @type {number} pointer id that owns the joystick drag (so the A button uses a different finger) */
  #dragPointerId = -1;
  /** @type {import('../common/direction.js').Direction} */
  #dir = DIRECTION.NONE;

  constructor() {
    super({ key: SCENE_KEYS.MOBILE_CONTROLS_SCENE });
  }

  /** @returns {void} */
  create() {
    this.#scale = this.#computeScale();
    this.#buildJoystick();
    this.#buildActionButton();
    this.#buildMenuButton();
    this.#layout();

    // Track the joystick drag with scene-level pointer events so the thumb keeps following the
    // finger even when it slides outside the base circle. Multi-touch safe: the A button uses its
    // own pointer; the joystick only reacts to the pointer that started its drag.
    this.input.on(Phaser.Input.Events.POINTER_DOWN, this.#onPointerDown, this);
    this.input.on(Phaser.Input.Events.POINTER_MOVE, this.#onPointerMove, this);
    this.input.on(Phaser.Input.Events.POINTER_UP, this.#onPointerUp, this);
    this.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.#onPointerUp, this);
    // multi-touch: allow two simultaneous pointers (joystick + action) plus the default one
    this.input.addPointer(2);

    this.scale.on(Phaser.Scale.Events.RESIZE, this.#onResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.off(Phaser.Input.Events.POINTER_DOWN, this.#onPointerDown, this);
      this.input.off(Phaser.Input.Events.POINTER_MOVE, this.#onPointerMove, this);
      this.input.off(Phaser.Input.Events.POINTER_UP, this.#onPointerUp, this);
      this.input.off(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.#onPointerUp, this);
      this.scale.off(Phaser.Scale.Events.RESIZE, this.#onResize, this);
      this.#setDirection(DIRECTION.NONE);
    });

    this.scene.bringToTop();
  }

  /**
   * The overworld touch controls must never sit over a battle. Regular battles `scene.start` the
   * BattleScene (which stops the world → shuts this overlay down), but a PvP duel only *pauses* the
   * world, leaving this overlay active. Hide it while the PvP battle scene is running (its own
   * pointer UI owns the screen) and restore it when the duel ends. Cheap per-frame visibility check.
   * @returns {void}
   */
  update() {
    const inPvp = this.scene.isActive(SCENE_KEYS.PVP_BATTLE_SCENE);
    // The world is only "driving" when it's active AND not paused. A menu / Map / shop / friends
    // overlay PAUSES it — and while paused a held joystick direction would otherwise be replayed the
    // instant the world resumes, auto-walking the player. So whenever we're NOT driving the world,
    // drop any in-flight drag + clear the direction (idempotent — #setDirection only fires on change).
    const driving =
      !inPvp &&
      this.scene.isActive(SCENE_KEYS.WORLD_SCENE) &&
      !this.scene.isPaused(SCENE_KEYS.WORLD_SCENE);
    if (!driving && (this.#dragging || this.#dir !== DIRECTION.NONE)) {
      this.#dragging = false;
      this.#dragPointerId = -1;
      this.#thumb?.setPosition(0, 0);
      this.#setDirection(DIRECTION.NONE);
    }

    if (inPvp !== this.#hidden) {
      this.#hidden = inPvp;
      [this.#joy, this.#actionBtn, this.#menuBtn].forEach((c) => c?.setVisible(!inPvp));
    }
  }

  /** @type {boolean} controls currently hidden (e.g. during a PvP duel) */
  #hidden = false;

  /** Narrow phones get smaller controls so they stay thumb-sized and clear of the HUD. */
  #computeScale() {
    const min = Math.min(this.scale.width, this.scale.height);
    if (min <= 380) {
      return 0.78;
    }
    if (min <= 480) {
      return 0.88;
    }
    return 1;
  }

  /** @returns {import('../utils/controls.js').Controls | undefined} */
  #worldControls() {
    const world = /** @type {import('./world-scene.js').WorldScene} */ (this.scene.get(SCENE_KEYS.WORLD_SCENE));
    return world?.controls;
  }

  /** Push the current direction into the world controls (idempotent — only on change). */
  #setDirection(dir) {
    if (dir === this.#dir) {
      return;
    }
    this.#dir = dir;
    this.#worldControls()?.setVirtualDirection(dir);
  }

  // ── Joystick ────────────────────────────────────────────────────────────────────

  /** @returns {void} */
  #buildJoystick() {
    const s = this.#scale;
    const baseR = JOY_BASE_R * s;
    const thumbR = JOY_THUMB_R * s;

    const base = this.add.circle(0, 0, baseR, GLASS, 0.42).setStrokeStyle(3, EMERALD, 0.7);
    this.#thumb = this.add.circle(0, 0, thumbR, EMERALD, 0.5).setStrokeStyle(2, 0xeafff5, 0.85);

    this.#joy = this.add.container(0, 0, [base, this.#thumb]).setScrollFactor(0).setDepth(DEPTH);
    // Drag START is handled at the SCENE level by proximity (see #onPointerDown) instead of a
    // GameObject hit area: a custom Circle hit area on a centre-origin Arc is offset to the object's
    // top-left, so taps on the visible circle missed it entirely (the "joystick not clickable" bug).
    // Proximity is also more forgiving (grab anywhere near the pad) and never mis-aligns on rescale.
  }

  /** @param {Phaser.Input.Pointer} pointer */
  #onPointerDown(pointer) {
    if (this.#hidden || this.#dragging || !this.#joy) {
      return;
    }
    // Start a drag if the press lands anywhere within a generous radius of the joystick pad.
    const grabR = JOY_BASE_R * this.#scale * 1.35;
    if (Math.hypot(pointer.x - this.#joy.x, pointer.y - this.#joy.y) <= grabR) {
      this.#dragging = true;
      this.#dragPointerId = pointer.id;
      this.#moveThumb(pointer);
    }
  }

  /**
   * Move the thumb toward the pointer (clamped to the base radius) and derive the dominant
   * 4-direction, with a small deadzone near the center.
   * @param {Phaser.Input.Pointer} pointer
   * @returns {void}
   */
  #moveThumb(pointer) {
    const s = this.#scale;
    const maxR = (JOY_BASE_R - JOY_THUMB_R * 0.4) * s;
    let dx = pointer.x - this.#joy.x;
    let dy = pointer.y - this.#joy.y;
    const dist = Math.hypot(dx, dy);
    if (dist > maxR && dist > 0) {
      dx = (dx / dist) * maxR;
      dy = (dy / dist) * maxR;
    }
    this.#thumb.setPosition(dx, dy);

    if (dist < DEADZONE * s) {
      this.#setDirection(DIRECTION.NONE);
      return;
    }
    // dominant axis → single 4-way direction
    if (Math.abs(dx) > Math.abs(dy)) {
      this.#setDirection(dx < 0 ? DIRECTION.LEFT : DIRECTION.RIGHT);
    } else {
      this.#setDirection(dy < 0 ? DIRECTION.UP : DIRECTION.DOWN);
    }
  }

  /** @param {Phaser.Input.Pointer} pointer */
  #onPointerMove(pointer) {
    if (this.#dragging && pointer.id === this.#dragPointerId) {
      this.#moveThumb(pointer);
    }
  }

  /** @param {Phaser.Input.Pointer} pointer */
  #onPointerUp(pointer) {
    if (this.#dragging && pointer.id === this.#dragPointerId) {
      this.#dragging = false;
      this.#dragPointerId = -1;
      this.#thumb.setPosition(0, 0); // recenter
      this.#setDirection(DIRECTION.NONE);
    }
  }

  // ── "A" action button (SPACE / interact / confirm) ───────────────────────────────

  /** @returns {void} */
  #buildActionButton() {
    const s = this.#scale;
    const r = ACTION_R * s;
    const bg = this.add.circle(0, 0, r, EMERALD, 0.32).setStrokeStyle(3, EMERALD, 0.85);
    const label = this.add
      .text(0, 0, 'A', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: `${Math.round(34 * s)}px`, color: '#eafff5' })
      .setOrigin(0.5);

    this.#actionBtn = this.add.container(0, 0, [bg, label]).setScrollFactor(0).setDepth(DEPTH);
    // Default (bounding-box) hit area — a custom Circle(0,0,r) on a centre-origin Arc is offset to
    // the top-left, so taps on the visible button missed. The square hit area is correctly aligned
    // and thumb-friendly.
    bg.setInteractive();
    // Fire on POINTER_DOWN for a snappy, immediate confirm (advance dialog / interact / OK).
    bg.on(Phaser.Input.Events.POINTER_DOWN, () => {
      this.#worldControls()?.pressVirtualConfirm();
      this.#pulse(this.#actionBtn);
    });
  }

  // ── "≡" menu button (opens the world menu) ───────────────────────────────────────

  /** @returns {void} */
  #buildMenuButton() {
    const s = this.#scale;
    const r = MENU_R * s;
    const bg = this.add.circle(0, 0, r, GLASS, 0.5).setStrokeStyle(2, EMERALD, 0.7);
    const label = this.add
      .text(0, -1, '≡', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: `${Math.round(26 * s)}px`, color: '#eafff5' })
      .setOrigin(0.5);

    this.#menuBtn = this.add.container(0, 0, [bg, label]).setScrollFactor(0).setDepth(DEPTH);
    // Default (bounding-box) hit area — a custom Circle(0,0,r) on a centre-origin Arc is offset to
    // the top-left, so taps on the visible button missed. The square hit area is correctly aligned
    // and thumb-friendly.
    bg.setInteractive();
    // ENTER toggles the world menu; the A button consumes SPACE, so a dedicated ENTER pulse is
    // needed to open it. pressVirtualConfirm() satisfies both space + enter (first caller wins),
    // so open the menu directly on the world scene instead to keep the A button's SPACE semantics.
    bg.on(Phaser.Input.Events.POINTER_DOWN, () => {
      const world = /** @type {import('./world-scene.js').WorldScene} */ (this.scene.get(SCENE_KEYS.WORLD_SCENE));
      if (world && typeof world.openMenu === 'function') {
        world.openMenu();
      } else {
        // fall back to the confirm pulse (world reads it as ENTER → toggles the keyboard menu)
        this.#worldControls()?.pressVirtualConfirm();
      }
      this.#pulse(this.#menuBtn);
    });
  }

  /** Quick press feedback. @param {Phaser.GameObjects.Container} c */
  #pulse(c) {
    c.setScale(0.9);
    this.time.delayedCall(90, () => c.setScale(1));
  }

  // ── Layout / reflow ───────────────────────────────────────────────────────────────

  /** @returns {void} */
  #onResize() {
    const next = this.#computeScale();
    if (next !== this.#scale) {
      // rebuild at the new scale (cheap — a handful of shapes)
      this.#scale = next;
      this.#joy.destroy();
      this.#actionBtn.destroy();
      this.#menuBtn.destroy();
      this.#dragging = false;
      this.#dragPointerId = -1;
      this.#setDirection(DIRECTION.NONE); // clear any held direction so the rebuild can't auto-walk
      this.#buildJoystick();
      this.#buildActionButton();
      this.#buildMenuButton();
    }
    this.#layout();
  }

  /**
   * Anchor: joystick bottom-LEFT (clear of the top-left player card + left icon menu — it sits in
   * the lower-left empty band), "A" bottom-RIGHT, "≡" just above "A". Reads this.scale.width/height.
   * @returns {void}
   */
  #layout() {
    const sw = this.scale.width;
    const sh = this.scale.height;
    const s = this.#scale;

    // Bottom inset = base bottom margin + the iOS home-indicator safe area, so the joystick and the
    // A/≡ buttons always sit clear of the home bar and the bottom HUD bands.
    const bottom = BOTTOM_MARGIN + safeAreaBottom();

    // Joystick: bottom-left, inset by its radius + margin.
    const joyR = JOY_BASE_R * s;
    this.#joy.setPosition(MARGIN + joyR, sh - bottom - joyR);

    // "A": bottom-right, inset by its radius + margin.
    const aR = ACTION_R * s;
    const ax = sw - MARGIN - aR;
    const ay = sh - bottom - aR;
    this.#actionBtn.setPosition(ax, ay);

    // "≡": stacked above "A".
    const mR = MENU_R * s;
    this.#menuBtn.setPosition(ax, ay - aR - mR - 14 * s);
  }
}
